import {LTRStickyStyles} from '../../components/timeslot-grid/constants'
import {CurrenciesEnum} from '../../types/currencies'
import {Area, AreaSelectArea} from '../area/types'
import {Class} from '../class/types'
import {Service} from '../service/types'
import {Media, OpeningTimes} from '../types'
import {Venue} from '../venue/types'

export enum CancelPolicyEnum {
  EASY = 'EASY',
  MEDIUM = 'MEDIUM',
  STRICT = 'STRICT',
  FLEXIBLE = 'FLEXIBLE',
  SEMI_FLEXIBLE = 'SEMI_FLEXIBLE',
}

export type InstructorBooking = {
  placeEntity: 'teacher'
  place: Instructor
}

export type ServiceBooking = {
  placeEntity: 'service'
  place: AreaSelectArea
}

export type BookingUsageDataType = {
  amountByType: 'AMOUNT_BY_CREDITS' | 'AMOUNT_BY_ENTRIES' | 'AMOUNT_BY_MINUTES'
  used: number
}

export type Booking = {
  uid: string
  name: string
  description?: string
  time: string
  area?: Area & {profileImage?: {secure_url: string}}
  media: Media[]
  venue: Venue
  date?: string
  sport: Service | null
  class?: Class
  isPaid: boolean
  entity: 'class' | 'service'
  userId: string
  refund?: number
  areaId?: string
  showInstructorDescription?: boolean
  venueId: string
  teacher?: Instructor
  currency: CurrenciesEnum
  duration: number | 'NO_DURATION'
  discount?: number
  teachers?: Instructor[]
  sportType: string
  reference: string
  timestamp: string
  areaSpace: boolean
  usageData?: BookingUsageDataType
  teacherId?: string
  classSlot?: Class & {areaName: string}
  price?: number
  totalPrice: number
  additionalFeeOrDiscount?: {price: number; isDiscount: boolean; label?: string} | null
  // D364: the booking's promo code; `totalPrice` is already after it.
  promoCode?: string | null
  promoDiscount?: number | null
  isCancelled: boolean
  paymentType?: string | null
  occupyUsage?: boolean
  cancelPolicy: CancelPolicyEnum | string
  activityName?: string
  splitPayment?: boolean
  participants: Participant[]
  date_timestamp?: number | string
  initialDiscount?: number
  specialRequests?: string
  paymentMethodId?: string | null
  spiltPrice?: boolean
  paymentOnExsportia?: boolean
  arrivalInstructions?: string
  splitPaymentExpireDate?: number
  allowUserToCancelBooking?: boolean
  cancelForPayOnLocationType?: 'DAYS' | 'HOURS'
  cancelForPayOnLocationDesc?: string
  cancelForPayOnLocationCount?: number
} & (InstructorBooking | ServiceBooking)

export type Participant = {
  equipments: Equipment[]
  avatar?: string
  name?: string
  isPaid?: boolean
  membershipId?: string | null
  userId?: string
  activityPrice?: number
  totalDiscount?: number
  equipmentsPrice?: number
  equipmentsTotalPrice?: number
  totalInitialDiscount?: number
  initialDiscount?: number
  membershipName?: string | null
  membershipBookingId?: string | null
  totalPrice?: number
  discount?: number
  usedCreditsWithSub?: number
  usedMinutesWithSub?: number
}

export type Equipment = {
  quantity: number
  price: number
  name: string
  label?: string
  value?: string
  // Add-ons library: a line that names an add-on. On a booking it is priced by the server;
  // `lineTotal` is its figure (the line's `price` is only the average each).
  addonId?: string
  lineTotal?: number
  kind?: string
  included?: boolean
  scope?: string
  tier?: number
}

export enum TimeslotStatesEnum {
  PAST = 'PAST',
  SELECTED = 'SELECTED',
  BLOCKED = 'BLOCKED',
  DEFAULT = 'DEFAULT',
  NOT_SELECTABLE = 'NOT_SELECTABLE',
}

export type TimeSlot = {
  moment: string
  date_timestamp: string
  spaces: number
  modified?: {
    participants?: Participant[]
    duration?: number
    status?: string
    bookings?: Booking[]
  }
  participants?: Participant[]
  options: {
    fakeValue?: number
    state: TimeslotStatesEnum
    stickyState?: keyof typeof LTRStickyStyles
  }
}

export type InstructorPickValues =
  'availableTimeStart' | 'description' | 'name' | 'openingTimes' | 'operatingHoursDiffer' | 'profileImage' | 'timeSlot'

// TODO: use unknown instead of any or idk just make it typesafe )
export type Instructor<T extends keyof Record<string, unknown> = InstructorPickValues> = Record<T, unknown> & {
  timeSlot: number
  uid: string
  name: string
  description?: string
  openingTimes: OpeningTimes
  availableTimeStart: number
  operatingHoursDiffer: boolean
  profileImage: {
    secure_url: string
  }
  address?: {
    addressDetails: string
    allData: {
      zip: string
      city: string
      state: string
      country: string
      address: string
      latitude: number
      longitude: number
      label: string
      formattedAddress: string
    }
    latLng: {
      lat: number
      lng: number
    }
  }
}

export type accessConfigType = {
  member?: boolean
  anySubscriptions?: boolean
  nonMember?: boolean
  subscribers?: boolean
  subscriptions: string[]
  permissionByTagsEnabled?: boolean
}

export type ServiceItem = {
  uid: string
  name: string
  description?: string
  icon: string // TODO: type icons
  price: number
  entity: 'service' | 'teacher'
  private: boolean
  minTime?: number
  maxTime?: number
  currency: CurrenciesEnum
  areasIds?: string[]
  arrivalInstructions?: string
  areaSpace?: boolean
  maxPlayers: number
  minPlayers: number
  withTimeLimit: boolean
  serviceType: string
  splitPayment?: boolean
  spiltPrice?: boolean
  sportType?: string
  fullPaymentTimer?: number
  showInstructorDescription?: boolean
  pricePeriod: number | 'NO_DURATION'
  cancelPolicy: CancelPolicyEnum
  // The venue's own cancellation policy, when the service names one (wins over the ladder step).
  venueCancelPolicyId?: string | null
  teachersIds?: string[]
  longSlotView?: boolean
  selectedAreas: string[]
  accessForAnyone?: boolean
  selectedTeachers: string[]
  multiPerTimeSlot: boolean
  paymentOnArrival?: boolean
  multiplyPerPerson: boolean
  participantsNames?: boolean
  minTimeReservation?: number
  maxTimeReservation?: number
  paymentOnExsportia?: boolean
  howFarCanBookUnlimited?: boolean
  participantsNamesWhenFull?: boolean
  accessConfig?: accessConfigType
  permissionData: {
    validated: boolean
    reason?:
      | 'FOR_MEMBERS_ONLY'
      | 'FOR_NON_MEMBERS_ONLY'
      | 'FOR_SUBSCRIBERS_ONLY'
      | 'NOT_AVAILABLE'
      | 'ONLY_WITHOUT_SUBSCRIPION'
  }
  equipments: {
    name: string
    price: number
  }[]
} & (
  | {
      howFarCanBookUnlimited: false
      howFarCanBookAmount: number
    }
  | {
      howFarCanBookUnlimited: true
    }
)

export type Playspace = {
  uid: string
  name: string
  image: string
  operatingHoursDiffer: boolean
  openingTimes: OpeningTimes
  availableTimeStart: number
  private: boolean
}

export type PlayspacesList = Playspace[]

export type WeekAvailableDays = boolean[]

export type InstructorsList = Instructor[]

export type TimeSlotGrid = TimeSlot[]

export type ServicesList = ServiceItem[]
